import React, { useEffect, useState, useRef } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import MemberEndorsementTable from '../../../components/tables/MemberEndorsementTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useMemberEndorsementApi } from '../../../hooks/useMemberEndorsementApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import { EndorsementLicenceTypeConstants } from '../../../constants/EndorsementLicenceTypeConstants';
import {
  AuthDTO,
  EmailMessageDTO,
  ImpersonationDTO,
  MemberEndorsementDTO,
  NoteDTO,
} from '../../../services/apiClient';
import { useNavigate, useLocation } from 'react-router-dom';
import { getRoles, getUserId } from '../../../utils/tokenUtils';
import { useSearchState } from '../../../utils/searchStateUtils/useSearchState';
import { useViewMode } from '../../../utils/viewModeUtils/useViewMode';
import { ViewToggle } from '../../../components/common/ViewToggle';
import MemberEndorsementDataCard from '../../../components/dataCards/MemberEndorsementDataCard';
import { ViewModeConstants } from '../../../constants/ViewModeConstants';
import { useAuthApi } from '../../../hooks/useAuthApi';
import { FileViewer } from '../../../components/common/FileViewer';
import { EndorsementStatusConstants } from '../../../constants/EndorsementStatusConstants';
import { MemberStatusConstants } from '../../../constants/MemberStatusConstants';
import Drawer from '../../../components/common/Drawer';
import FeedbackForm from '../../../components/forms/FeedbackForm';
import { ComponentConstants } from '../../../constants/ComponentConstants';
import { DrawerViewConstants, MemberEndorsementDrawerViews } from '../../../constants/DrawerViewConstants';
import { useEndorsementStatuses } from '../../../hooks/useEndorsementStatusApi';
import { useEmailMessageApi } from '../../../hooks/useEmailMessageApi';
import EmailDrawerContent from '../../../components/common/EmailDrawerContent';
import { useEmailHandlers } from '../../../hooks/useEmailHandlers';
import { useNoteApi } from '../../../hooks/useNoteApi';
import AddNote from '../../../components/drawer/AddNote';
import ViewNoteHistory from '../../../components/drawer/ViewNoteHistory';
import MemberQuickDetails from '../../../components/drawer/MemberQuickDetails';
import { useImpersonation } from '../../../hooks/useImpersonation';
import GeneralSearch from '../../../components/common/GeneralSearch';
import { ChevronDown } from 'lucide-react';
import { getAdjustedPageAfterMutation } from '../../../utils/paginationUtils';
import ButtonLegendCta from '../../../components/common/ButtonLegendCta';
import { getButtonLegendItems } from '../../../utils/buttonLegendDisplayUtils';
//import ExportButton from '../../../components/common/ExportButton';

const ENDORSEMENT_TYPES = {
  NEW: 'A07ABE24-FC19-446E-BFC3-6D687341A86B',
  RENEWAL: 'F2C82F77-A276-40F2-867A-C579A968BAFB',
};

const legendItems = getButtonLegendItems('memberEndorsement');

const MemberEndorsementList: React.FC = () => {
  const { search, isLoading, updateStatus, getDistinctYears } = useMemberEndorsementApi();
  const { edit, remove, extract, restoreFromDeleted, getCertificate, getDeclaration, error } =
    useMemberEndorsementApi();
  const { data: endorsementStatusesData } = useEndorsementStatuses();
  const [items, setItems] = useState<MemberEndorsementDTO[]>([]);
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [endorsementStatuses, setEndorsementStatuses] = useState<
    { value: string; label: string }[]
  >([]);

  // Search state persistence
  const {
    currentPage,
    pageSize,
    sortBy,
    sortDescending,
    searchFilters,
    showAdvanced,
    setCurrentPage,
    setPageSize,
    setSortBy,
    setSortDescending,
    setSort,
    setSearchFilters,
    setShowAdvanced,
    commitSearch,
    clearSearchState,
  } = useSearchState('memberendorsements', {
    currentPage: 1,
    pageSize: 10,
    sortBy: 'CreatedUtc',
    sortDescending: true,
    searchFilters: {},
    showAdvanced: false,
  });

  const [totalRecords, setTotalRecords] = useState(0);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [drawerView, setDrawerView] = useState<MemberEndorsementDrawerViews>();
  const [selectedEndorsement, setSelectedEndorsement] = useState<MemberEndorsementDTO>({});
  const [defaultStatusId, setDefaultStatusId] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [drawerTitle, setDrawerTitle] = useState<string>('');
  const [drawerSize, setDrawerSize] = useState<'small' | 'large'>('large');
  const [selectedEndorsementIds, setSelectedEndorsementIds] = useState<Set<string>>(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);
  const bulkActionsRef = useRef<HTMLDivElement>(null);
  const endorsementStatusId = 'statusId';
  const roles = getRoles();
  const { sendEmail, isLoading: isSendingEmail } = useEmailMessageApi();
  const { create: createNote, isLoading: isSubmittingNote } = useNoteApi();

  const { view, toggle } = useViewMode(
    'member-endorsements',
    roles?.includes('User') ? ViewModeConstants.DATA_CARDS : ViewModeConstants.TABLE,
  );

  const navigate = useNavigate();
  const location = useLocation();

  // Get status from URL
  const statusFromUrl = location.pathname.includes('/status/')
    ? location.pathname.split('/status/')[1].toLowerCase()
    : '';

  // Clear items when route variant changes (prevents showing stale data from previous page)
  const previousPathnameRef = useRef(location.pathname);
  useEffect(() => {
    if (previousPathnameRef.current !== location.pathname) {
      setItems([]); // Clear old data immediately
      previousPathnameRef.current = location.pathname;
    }
  }, [location.pathname]);

  // Function to get filters based on status
  const getFiltersFromStatus = (status: string) => {
    switch (status.toLowerCase()) {
      case 'new':
        return {
          licenceTypeId: ENDORSEMENT_TYPES.NEW,
          isDeleted: false,
        };
      case 'renewal':
        return {
          licenceTypeId: ENDORSEMENT_TYPES.RENEWAL,
          isDeleted: false,
        };
      case 'deleted':
        return { isDeleted: true };
      default:
        return { isDeleted: false };
    }
  };

  // Function to get display name for status
  const getStatusDisplayName = (status: string) => {
    switch (status.toLowerCase()) {
      case 'new':
        return 'New Applications';
      case 'renewal':
        return 'Renewals';
      case 'deleted':
        return 'Deleted';
      default:
        return 'All Endorsements';
    }
  };

  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Endorsements', href: '#' },
    ...(statusFromUrl ? [{ label: getStatusDisplayName(statusFromUrl), href: '#' }] : []),
  ];

  // Reset then reload years when route changes
  useEffect(() => {
    setSelectedYear(null);
    setAvailableYears([]);
    if (!roles?.includes('User')) return;
    getDistinctYears().then((years) => {
      setAvailableYears(years);
      if (years.length > 0) setSelectedYear(years[0]);
    });
  }, [statusFromUrl]);

  // Update fetchItems to include status filters and year range
  const fetchItems = async (page: number, size: number, filters: any) => {
    try {
      const statusFilters = statusFromUrl
        ? getFiltersFromStatus(statusFromUrl)
        : { isDeleted: false };

      const yearFilters = selectedYear
        ? {
          createdFrom: new Date(selectedYear, 0, 1).toISOString(),
          createdTo: new Date(selectedYear, 11, 31, 23, 59, 59).toISOString(),
        }
        : {};

      const response = await search({
        filters: {
          ...filters,
          ...statusFilters,
          ...yearFilters,
        },
        page,
        pageSize: size,
        sortBy: sortBy,
        sortDescending: sortDescending,
      });
      setItems(response?.data?.results || []);
      setTotalRecords(response?.data?.totalCount || 0);
      return response?.data?.totalCount || 0;
    } catch (error) {
      console.error('Error fetching endorsements:', error);
      return 0;
    }
  };

  useEffect(() => {
    setEndorsementStatuses(
      endorsementStatusesData?.map((s) => ({
        value: s.id ?? '',
        label: s.title ?? '',
      })) || [],
    );
  }, [endorsementStatusesData]);

  const handleSort = (sortKey: string) => {
    if (sortBy === sortKey) {
      setSort(sortKey, !sortDescending);
    } else {
      setSort(sortKey, true);
    }
  };

  /**
   * Refreshes the list after a mutation (delete, update, etc.),
   * automatically adjusting to a valid page if the current page is now out of range.
   */
  const refreshAfterMutation = async () => {
    // Fetch with current page first to get the new total count
    const newTotalCount = await fetchItems(currentPage, pageSize, searchFilters);

    // Calculate what page we should be on based on new total
    const adjustedPage = getAdjustedPageAfterMutation(currentPage, pageSize, newTotalCount);

    // If we need to move to a different page, update and refetch
    if (adjustedPage !== currentPage) {
      setCurrentPage(adjustedPage);
      await fetchItems(adjustedPage, pageSize, searchFilters);
    }
  };

  const previousFiltersRef = useRef<string>('');
  const isCommittingSearchRef = useRef<boolean>(false);
  const yearInitialisedRef = useRef<boolean>(false);

  // Refetch when selected year changes
  useEffect(() => {
    if (selectedYear === null) {
      if (yearInitialisedRef.current) {
        yearInitialisedRef.current = false;
        setCurrentPage(1);
        fetchItems(1, pageSize, searchFilters);
      }
      return;
    }
    yearInitialisedRef.current = true;
    setCurrentPage(1);
    fetchItems(1, pageSize, searchFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  // Sync check: get current URL filters on every render to detect mismatches
  const getCurrentUrlFilters = () => {
    const params = new URLSearchParams(location.search);
    const reservedParams = ['page', 'pageSize', 'sortBy', 'sortDesc', 'showAdvanced', 'view'];
    const urlFilters: Record<string, string> = {};
    params.forEach((value, key) => {
      if (!reservedParams.includes(key) && value) {
        urlFilters[key] = value;
      }
    });
    return JSON.stringify(urlFilters);
  };

  const currentUrlFiltersKey = getCurrentUrlFilters();
  const areFiltersStale = items.length > 0 && previousFiltersRef.current !== currentUrlFiltersKey;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const reservedParams = ['page', 'pageSize', 'sortBy', 'sortDesc', 'showAdvanced', 'view'];
    const urlFilters: Record<string, string> = {};

    params.forEach((value, key) => {
      if (!reservedParams.includes(key) && value) {
        urlFilters[key] = value;
      }
    });

    const currentFiltersKey = JSON.stringify(urlFilters);

    // Skip this effect if we're in the middle of a commitSearch AND the URL hasn't actually changed yet
    if (isCommittingSearchRef.current && currentFiltersKey === previousFiltersRef.current) {
      return;
    }

    // URL has now updated, clear the flag
    if (isCommittingSearchRef.current) {
      isCommittingSearchRef.current = false;
    }

    // Parse page and pageSize from URL (source of truth) to avoid stale state
    const urlPage = params.get('page');
    const urlPageSize = params.get('pageSize');
    const page = urlPage ? parseInt(urlPage, 10) : 1;
    const size = urlPageSize ? parseInt(urlPageSize, 10) : pageSize;

    // If URL has no filter params but searchFilters state has some, clear the state
    // This handles the case where user navigates between routes and searchFilters is stale
    const hasUrlFilters = Object.keys(urlFilters).length > 0;
    const hasStateFilters = searchFilters && Object.keys(searchFilters).length > 0;
    if (!hasUrlFilters && hasStateFilters) {
      setSearchFilters({});
    }

    // Clear items immediately if filters changed (not just page number)
    if (previousFiltersRef.current !== currentFiltersKey) {
      setItems([]);
      previousFiltersRef.current = currentFiltersKey;
    }

    fetchItems(page, size, urlFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, statusFromUrl]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchItems(1, pageSize, searchFilters);
  };

  const handleMemberClick = (memberId: string) => {
    // Find the endorsement with this memberId to get the member's ID/Passport number
    const endorsement = items.find(item => item.memberId === memberId);
    const idPassportNumber = endorsement?.member?.idPassportNumber || '';

    // Set flag to prevent intermediate useEffect from running with stale URL
    isCommittingSearchRef.current = true;

    // Endorsement search textbox uses idNumber; clear all other filters and search only by ID.
    const newFilters = idPassportNumber ? { idNumber: idPassportNumber } : {};
    // The useEffect watching location.search will trigger the fetch automatically
    commitSearch(newFilters, { page: 1, showAdvanced: false, replace: false });
  };

  const handleClearSearch = () => {
    clearSearchState();
    fetchItems(1, pageSize, {});
  };

  const handleDelete = async (dto: MemberEndorsementDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await remove(dto.id || '');
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error deleting MemberEndorsement:', error);
    }
  };

  // Email handlers from useEmailHandlers hook
  const {
    handleSendEmail,
    handleSendEmailSubmit,
    handleViewAllEmails,
    handleViewRecordEmails,
  } = useEmailHandlers<MemberEndorsementDTO>({
    setSelectedItem: setSelectedEndorsement,
    setDrawerTitle,
    setDrawerView,
    setIsDrawerOpen,
    sendEmail,
    fetchItems: async (page: number, size: number, filters: any) => {
      await fetchItems(page, size, filters);
    },
    currentPage,
    pageSize,
    searchFilters,
  });

  // Add export handler
  const handleExport = async () => {
    try {
      // Mirror the filters applied by fetchItems so the export matches the visible list
      const statusFilters = statusFromUrl
        ? getFiltersFromStatus(statusFromUrl)
        : { isDeleted: false };

      const yearFilters = selectedYear
        ? {
          createdFrom: new Date(selectedYear, 0, 1).toISOString(),
          createdTo: new Date(selectedYear, 11, 31, 23, 59, 59).toISOString(),
        }
        : {};

      const response = await extract({
        filters: {
          ...searchFilters,
          ...statusFilters,
          ...yearFilters,
        },
        page: 1,
        pageSize: 9999999, // Large number to export all matching records
        sortBy: sortBy,
        sortDescending: sortDescending,
      });

      if (response.data) {
        window.location.href = response.data;
      }
    } catch (error) {
      console.error('Error exporting Member Endorsements:', error);
    }
  };

  const handleRestore = async (dto: MemberEndorsementDTO) => {
    try {
      await restoreFromDeleted(dto.id || '');
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error restoring endorsement:', error);
    }
  };

  const handleOpenDrawer = async (
    endorsement: MemberEndorsementDTO,
    drawerView: MemberEndorsementDrawerViews,
    drawerTitle: string,
    drawerSize: 'small' | 'large' = 'large',
    defaultStatusId?: string,
  ) => {
    setSelectedEndorsement(endorsement);
    setDrawerView(drawerView);
    setDrawerTitle(drawerTitle);
    setDrawerSize(drawerSize);
    if (defaultStatusId) setDefaultStatusId(defaultStatusId);
    setIsDrawerOpen(true);
  };

  const handleAddNote = async (data: NoteDTO) => {
    try {
      setIsSubmitting(true);
      await createNote(data);
      setIsDrawerOpen(false);
      setSelectedEndorsement({});
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error creating note: ', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const { impersonate } = useImpersonation();
  const [viewingFile, setViewingFile] = useState<{ name: string; url: string } | null>(null);

  const handleGenerateCertificate = async (row: MemberEndorsementDTO) => {
    try {
      const certificateUrl = await getCertificate(row.id ?? '');
      const filename = `Endorsement-Certificate(${row.id ?? ''}).pdf`;
      setViewingFile({
        name: filename,
        url: certificateUrl,
      });
    } catch (error) {
      console.error('Error generating certificate:', error);
    }
  };

  const handleGenerateDeclaration = async (row: MemberEndorsementDTO) => {
    try {
      const declarationUrl = await getDeclaration(row.id ?? '');
      const filename = `Endorsement-Declaration(${row.id ?? ''}).pdf`;
      setViewingFile({
        name: filename,
        url: declarationUrl,
      });
    } catch (error) {
      console.error('Error generating declaration:', error);
    }
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setSelectedEndorsement({});
    setDefaultStatusId(undefined);
    setDrawerSize('large');
  };

  const handleSelectRow = (id: string, isSelected: boolean) => {
    setSelectedEndorsementIds(prev => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  };

  const handleSelectAll = (isSelected: boolean) => {
    if (isSelected) {
      const allIds = items.map(item => item.id).filter((id): id is string => !!id);
      setSelectedEndorsementIds(new Set(allIds));
    } else {
      setSelectedEndorsementIds(new Set());
    }
  };

  const handleBulkApprove = () => {
    const firstSelectedId = Array.from(selectedEndorsementIds)[0];
    const firstEndorsement = items.find(e => e.id === firstSelectedId) || {};
    const count = selectedEndorsementIds.size;
    handleOpenDrawer(
      firstEndorsement as MemberEndorsementDTO,
      DrawerViewConstants.FEEDBACK,
      `Approve (${count} endorsement${count > 1 ? 's' : ''})`,
      'large',
      EndorsementStatusConstants.APPROVED
    );
  };

  const handleBulkApproveWithFeedback = () => {
    const firstSelectedId = Array.from(selectedEndorsementIds)[0];
    const firstEndorsement = items.find(e => e.id === firstSelectedId) || {};
    const count = selectedEndorsementIds.size;
    handleOpenDrawer(
      firstEndorsement as MemberEndorsementDTO,
      DrawerViewConstants.FEEDBACK,
      `Approve with Feedback (${count} endorsement${count > 1 ? 's' : ''})`,
      'large',
      EndorsementStatusConstants.APPROVED_WITH_FEEDBACK
    );
  };

  const handleBulkReviewWithFeedback = () => {
    const firstSelectedId = Array.from(selectedEndorsementIds)[0];
    const firstEndorsement = items.find(e => e.id === firstSelectedId) || {};
    const count = selectedEndorsementIds.size;
    handleOpenDrawer(
      firstEndorsement as MemberEndorsementDTO,
      DrawerViewConstants.FEEDBACK,
      `Review with Feedback (${count} endorsement${count > 1 ? 's' : ''})`,
      'large',
      EndorsementStatusConstants.REVIEWED_WITH_FEEDBACK
    );
  };

  const handleBulkDecline = () => {
    const firstSelectedId = Array.from(selectedEndorsementIds)[0];
    const firstEndorsement = items.find(e => e.id === firstSelectedId) || {};
    const count = selectedEndorsementIds.size;
    handleOpenDrawer(
      firstEndorsement as MemberEndorsementDTO,
      DrawerViewConstants.FEEDBACK,
      `Decline (${count} endorsement${count > 1 ? 's' : ''})`,
      'large',
      EndorsementStatusConstants.DECLINED
    );
  };

  const handleBulkOutstanding = () => {
    const firstSelectedId = Array.from(selectedEndorsementIds)[0];
    const firstEndorsement = items.find(e => e.id === firstSelectedId) || {};
    const count = selectedEndorsementIds.size;
    handleOpenDrawer(
      firstEndorsement as MemberEndorsementDTO,
      DrawerViewConstants.FEEDBACK,
      `Mark Outstanding (${count} endorsement${count > 1 ? 's' : ''})`,
      'large',
      EndorsementStatusConstants.PENDING
    );
  };

  const handleBulkStatusUpdate = () => {
    const firstSelectedId = Array.from(selectedEndorsementIds)[0];
    const firstEndorsement = items.find(e => e.id === firstSelectedId) || {};
    const count = selectedEndorsementIds.size;
    handleOpenDrawer(
      firstEndorsement as MemberEndorsementDTO,
      DrawerViewConstants.FEEDBACK,
      `Update Status (${count} endorsement${count > 1 ? 's' : ''})`
    );
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (bulkActionsRef.current && !bulkActionsRef.current.contains(event.target as Node)) {
        setShowBulkActions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleStatusSubmit = async (data: any) => {
    try {
      setIsSubmitting(true);

      // Determine which endorsement IDs to update
      const idsToUpdate = selectedEndorsementIds.size > 0
        ? Array.from(selectedEndorsementIds)
        : [selectedEndorsement.id!];

      // Prepare bulk update DTO
      const updateDto = {
        recordIds: idsToUpdate,
        endorsementStatusId: data.statusId,
        feedback: data.feedback,
        replyTemplateId: data.replyTemplateId,
        sendFeedbackEmail: data.sendFeedbackEmail || false,
      };

      // Call the bulk update endpoint
      await updateStatus(updateDto);

      setIsDrawerOpen(false);
      setSelectedEndorsement({});
      setSelectedEndorsementIds(new Set()); // Clear selections
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error submitting feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminLayout
      activeMenuItem={
        statusFromUrl
          ? `/admin/memberendorsements/status/${statusFromUrl}`
          : '/admin/memberendorsements'
      }
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin', 'User']}
      requiredPermission="Manage.MemberEndorsement.View"
    >
      <Card
        title={`Manage Member Endorsements ${statusFromUrl ? `- ${getStatusDisplayName(statusFromUrl)}` : ''
          }`}
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <div className="flex items-center space-x-2">
            {roles?.includes('Admin') && selectedEndorsementIds.size > 0 && (
              <div ref={bulkActionsRef} className="relative">
                <Button
                  variant="secondary"
                  onClick={() => setShowBulkActions(!showBulkActions)}
                  className="flex items-center gap-2"
                >
                  Bulk Actions
                  <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold rounded-full">
                    {selectedEndorsementIds.size}
                  </span>
                  <ChevronDown
                    size={16}
                    className={`transition-transform duration-200 ${showBulkActions ? 'rotate-180' : ''
                      }`}
                  />
                </Button>
                {showBulkActions && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10">
                    <div className="py-1">
                      <button
                        onClick={() => {
                          handleBulkStatusUpdate();
                          setShowBulkActions(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 first:rounded-t-lg"
                      >
                        Update Status
                      </button>
                      <button
                        onClick={() => {
                          handleBulkApprove();
                          setShowBulkActions(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => {
                          handleBulkApproveWithFeedback();
                          setShowBulkActions(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        Approve with Feedback
                      </button>
                      <button
                        onClick={() => {
                          handleBulkReviewWithFeedback();
                          setShowBulkActions(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        Review with Feedback
                      </button>
                      <button
                        onClick={() => {
                          handleBulkDecline();
                          setShowBulkActions(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        Decline
                      </button>
                      <button
                        onClick={() => {
                          handleBulkOutstanding();
                          setShowBulkActions(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 last:rounded-b-lg"
                      >
                        Mark Outstanding
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {roles?.includes('Admin') && (
              <>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowAdvanced(!showAdvanced);
                  }}
                >
                  {showAdvanced ? 'Basic Search' : 'Advanced Search'}
                </Button>
                <Button type="button" variant="secondary" onClick={handleExport}>
                  Export
                </Button>
              </>
            )}

            {roles?.includes('User') && (
              <>
                <ButtonLegendCta items={legendItems} />
                <AddNewButton label='Apply for an Endorsement' onClick={() => navigate('/admin/memberendorsements/create')} />
              </>
            )}
          </div>
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          {showAdvanced && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <FormField
                id="firstName"
                label="Search by First Name"
                value={searchFilters.firstName || ''}
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    firstName: e.target.value,
                  })
                }
                placeholder="Search by First Name"
              />
              <FormField
                id="surname"
                label="Search by Surname"
                value={searchFilters.surname || ''}
                onChange={(e) => setSearchFilters({ ...searchFilters, surname: e.target.value })}
                placeholder="Search by Surname"
              />
              <FormField
                id="idNumber"
                label="Search by ID Number"
                value={searchFilters.idNumber || ''}
                onChange={(e) => setSearchFilters({ ...searchFilters, idNumber: e.target.value })}
                placeholder="Search by ID Number"
              />
              <FormField
                id="statusId"
                label="Search by Status"
                value={searchFilters.statusId || ''}
                type='dropdown'
                onChange={(e) => setSearchFilters({ ...searchFilters, statusId: e.target.value })}
                placeholder="Search by Status"
                options={[...endorsementStatuses.map((status) => ({ value: status.value, label: status.label })), { value: 'Deleted', label: 'Deleted' }]}
              />
              <FormField
                id="createdFrom"
                label="Created From"
                type="date"
                value={searchFilters.createdFrom || ''}
                onChange={(e) =>
                  setSearchFilters({ ...searchFilters, createdFrom: e.target.value })
                }
              />
              <FormField
                id="createdTo"
                label="Created To"
                type="date"
                value={searchFilters.createdTo || ''}
                onChange={(e) =>
                  setSearchFilters({ ...searchFilters, createdTo: e.target.value })
                }
              />
            </div>
          )}

          <div className="flex justify-between items-center">
            {roles?.includes('User') && availableYears.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {availableYears.map((year) => (
                  <Button
                    key={year}
                    variant={selectedYear === year ? 'primary' : 'secondary'}
                    onClick={() => setSelectedYear(year)}
                  >
                    {year}
                  </Button>
                ))}
              </div>
            )}
            <div className="space-x-4 mb-4">
              {showAdvanced && (
                <>
                  <Button variant="secondary" onClick={handleClearSearch}>
                    Clear Search
                  </Button>
                  <Button type="submit">Search</Button>
                </>
              )}
            </div>

            {!showAdvanced && roles?.includes('Admin') && (
              <GeneralSearch
                searchFilters={searchFilters}
                setSearchFilters={setSearchFilters}
              />
            )}

            {roles?.includes('User') && (
              <div className="hidden md:block">
                <ViewToggle view={view} toggle={toggle} />
              </div>
            )}
          </div>
        </form>



        {roles?.includes('Admin') || view === ViewModeConstants.TABLE ? (
          <MemberEndorsementTable
            data={areFiltersStale ? [] : items}
            handleViewMemberDetails={(dto) => handleOpenDrawer(dto, DrawerViewConstants.VIEW_MEMBER_DETAILS, 'Member Details', 'small')}
            handleEdit={(dto) => navigate(`/admin/memberendorsements/${dto.id}`)}
            handleDelete={handleDelete}
            handleSendEmail={handleSendEmail}
            handleViewAllEmails={handleViewAllEmails}
            handleViewRecordEmails={handleViewRecordEmails}
            handleRestore={handleRestore}
            handleApprove={(dto) =>
              handleOpenDrawer(dto, DrawerViewConstants.FEEDBACK, 'Approve', 'large', EndorsementStatusConstants.APPROVED)
            }
            handleChangeStatus={(dto) =>
              handleOpenDrawer(dto, DrawerViewConstants.FEEDBACK, 'Change Status')
            }
            handleImpersonateMemberLogin={(dto) => impersonate(dto.memberId, dto.member?.memberStatusId)}
            handleGenerateCertificate={handleGenerateCertificate}
            handleAddNote={(dto) =>
              handleOpenDrawer(dto, DrawerViewConstants.ADD_NOTE, 'Add Note')
            }
            handleViewNoteHistory={(dto) =>
              handleOpenDrawer(dto, DrawerViewConstants.VIEW_NOTE_HISTORY, 'Notes History')
            }
            handleMemberClick={handleMemberClick}
            currentPage={currentPage}
            totalPages={Math.ceil(totalRecords / pageSize)}
            setCurrentPage={setCurrentPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            isLoading={isLoading}
            sortBy={sortBy}
            sortDescending={sortDescending}
            onSort={handleSort}
            {...(roles?.includes('Admin') ? {
              selectedIds: selectedEndorsementIds,
              onSelectRow: handleSelectRow,
              onSelectAll: handleSelectAll,
            } : {})}
          />
        ) : (
          <MemberEndorsementDataCard
            data={items}
            handleEdit={(dto) => navigate(`/admin/memberendorsements/${dto.id}`)}
            handleDelete={handleDelete}
            handleGenerateCertificate={handleGenerateCertificate}
            handleGenerateDeclaration={handleGenerateDeclaration}
            currentPage={currentPage}
            totalPages={Math.ceil(totalRecords / pageSize)}
            setCurrentPage={setCurrentPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            isLoading={isLoading}
          />
        )}
      </Card>

      {viewingFile && (
        <FileViewer
          fileName={viewingFile.name}
          sasUrl={viewingFile.url}
          onClose={() => setViewingFile(null)}
        />
      )}

      <Drawer isOpen={isDrawerOpen} onClose={handleDrawerClose} title={drawerTitle} size={drawerSize}>
        {drawerView === DrawerViewConstants.VIEW_MEMBER_DETAILS && (
          <MemberQuickDetails
            memberId={selectedEndorsement.member?.id ?? ''}
            onClose={handleDrawerClose}
          />
        )}
        {drawerView === DrawerViewConstants.FEEDBACK && (
          <FeedbackForm
            componentId={ComponentConstants.ENDORSEMENTS}
            recordId={selectedEndorsement.id ?? ''}
            componentStatuses={endorsementStatuses}
            statusFieldName={endorsementStatusId}
            defaultStatusId={defaultStatusId}
            initialData={selectedEndorsement}
            onSubmit={handleStatusSubmit}
            onCancel={handleDrawerClose}
            updateLoading={isSubmitting}
          />
        )}
        {drawerView === DrawerViewConstants.ADD_NOTE && (
          <AddNote
            memberId={selectedEndorsement.member?.id || ''}
            componentId={ComponentConstants.ENDORSEMENTS}
            recordId={selectedEndorsement.id ?? ''}
            onSubmit={handleAddNote}
            onCancel={handleDrawerClose}
            isLoading={isSubmitting}
          />
        )}
        {drawerView === DrawerViewConstants.VIEW_NOTE_HISTORY && (
          <ViewNoteHistory memberId={selectedEndorsement.member?.id || ''} />
        )}

        <EmailDrawerContent
          drawerView={drawerView}
          selectedItem={selectedEndorsement}
          componentId={ComponentConstants.ENDORSEMENTS}
          getMemberId={(dto) => dto?.member?.id || ''}
          onEmailSubmit={handleSendEmailSubmit}
          onClose={handleDrawerClose}
          isSending={isSendingEmail}
        />
      </Drawer>
    </AdminLayout>
  );
};

export default MemberEndorsementList;
